// src/components/opportunities/OpportunityDetailModal.tsx
// Opportunity detail and edit modal

import { useState, useEffect } from 'react';
import { useGetOpportunityQuery, useUpdateOpportunityMutation, useUpdateOpportunityStageMutation } from '../../api/opportunitiesApi';
import { useGetContactsQuery } from '../../api/contactsApi';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import LoadingSpinner from '../common/LoadingSpinner';
// PitchLess materials. An opportunity is the deal a pitch deck or one-pager
// is made FOR, so it is one of the three records a material can come from.
import { MaterialsPanel } from '../materials/MaterialsPanel';
import { useHasCapability } from '../../services/centralContext';
import { FEATURE_CAPABILITIES } from '../../config/featureCapabilities';
import type { Opportunity, OpportunityStatus, PipelineStage } from '../../types/api';
import { describeDealFailure } from './dealFailure';

const STATUS_COLORS: Record<OpportunityStatus, { bg: string; text: string }> = {
  open: { bg: 'bg-blue-100', text: 'text-blue-700' },
  won: { bg: 'bg-green-100', text: 'text-green-700' },
  lost: { bg: 'bg-red-100', text: 'text-red-700' },
};

interface OpportunityDetailModalProps {
  opportunityId: string | null;
  workspaceId: string;
  stages: PipelineStage[];
  onClose: () => void;
  /**
   * When set, the deal's stage cannot change and this says why — the deal
   * belongs to an ARCHIVED pipeline, whose deals the server refuses to move.
   * Its other fields stay editable.
   */
  stageReadOnlyReason?: string | null;
}

export default function OpportunityDetailModal({
  opportunityId,
  workspaceId,
  stages,
  onClose,
  stageReadOnlyReason = null,
}: OpportunityDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  // FU-2 — `deals:write`, the same capability routes/opportunities.ts requires
  // for PATCH /:id. Read unconditionally; hooks cannot sit behind a branch.
  const canWriteDeals = useHasCapability(
    FEATURE_CAPABILITIES.editDeal.domain,
    FEATURE_CAPABILITIES.editDeal.action
  );
  const [formData, setFormData] = useState<Partial<Opportunity & { stageId: string }>>({});
  // A refused save stays in edit mode with the reason shown — the same
  // mapping the Pipeline board uses. It used to reach only the console.
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    data: opportunity,
    isLoading,
    error,
  } = useGetOpportunityQuery(opportunityId!, { skip: !opportunityId });

  const { data: contactsResponse } = useGetContactsQuery({ workspaceId }, { skip: !workspaceId });
  const contacts = contactsResponse?.contacts;

  const [updateOpportunity, { isLoading: isUpdating }] = useUpdateOpportunityMutation();
  const [updateStage, { isLoading: isUpdatingStage }] = useUpdateOpportunityStageMutation();

  const isSaving = isUpdating || isUpdatingStage;

  // Initialize form data when opportunity loads
  useEffect(() => {
    if (opportunity) {
      setFormData({
        title: opportunity.title,
        description: opportunity.description,
        amount: opportunity.amount,
        stageId: opportunity.stageId,
        expectedCloseDate: opportunity.expectedCloseDate,
        contactId: opportunity.contactId,
      });
    }
  }, [opportunity]);

  const handleSave = async () => {
    if (!opportunityId || !opportunity) return;

    setSaveError(null);
    let step: 'update' | 'move' = 'update';
    try {
      // Update opportunity basic fields
      await updateOpportunity({
        id: opportunityId,
        title: formData.title,
        description: formData.description || null,
        amount: formData.amount ?? null,
        expectedCloseDate: formData.expectedCloseDate || null,
        contactId: formData.contactId || null,
      }).unwrap();

      // Update stage if changed
      if (!stageReadOnlyReason && formData.stageId && formData.stageId !== opportunity.stageId) {
        step = 'move';
        await updateStage({ id: opportunityId, stageId: formData.stageId }).unwrap();
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update opportunity:', error);
      setSaveError(describeDealFailure(error, step));
    }
  };

  const handleCancel = () => {
    if (opportunity) {
      setFormData({
        title: opportunity.title,
        description: opportunity.description,
        amount: opportunity.amount,
        stageId: opportunity.stageId,
        expectedCloseDate: opportunity.expectedCloseDate,
        contactId: opportunity.contactId,
      });
    }
    setSaveError(null);
    setIsEditing(false);
  };

  // Format date for input
  const formatDateForInput = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toISOString().split('T')[0];
  };

  // Sort stages by order
  const sortedStages = [...stages].sort((a, b) => a.order - b.order);

  return (
    <Modal
      isOpen={!!opportunityId}
      onClose={onClose}
      title={isEditing ? 'Edit Deal' : 'Deal Details'}
      size="lg"
      footer={
        isEditing ? (
          <>
            <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={isSaving}>
              Save Changes
            </Button>
          </>
        ) : (
          // Editing a deal is `deals:write` (routes/opportunities.ts, PATCH
          // /:id). A reader with only `deals:read` was offered this, and
          // pressing Save was a 403. The Materials panel below stays visible
          // either way — reading material status and review state is exactly
          // the reviewer workflow this must not remove.
          canWriteDeals ? (
            <Button onClick={() => setIsEditing(true)}>Edit Deal</Button>
          ) : null
        )
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-500">Failed to load deal details.</p>
        </div>
      ) : opportunity ? (
        <div className="space-y-6">
          {isEditing && saveError && (
            <p
              role="alert"
              data-testid="deal-save-error"
              className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
            >
              {saveError}
            </p>
          )}
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <p className="text-lg font-semibold text-gray-900">{opportunity.title}</p>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Value</label>
            {isEditing ? (
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount || ''}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || undefined })}
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
            ) : (
              <p className="text-2xl font-bold text-gray-900">
                {opportunity.amount
                  ? `$${opportunity.amount.toLocaleString()}`
                  : 'No value set'}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            {isEditing ? (
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <p className="text-gray-900 whitespace-pre-wrap">
                {opportunity.description || 'No description'}
              </p>
            )}
          </div>

          {/* Status & Stage */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <span
                className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${STATUS_COLORS[opportunity.status].bg} ${STATUS_COLORS[opportunity.status].text}`}
              >
                {opportunity.status.charAt(0).toUpperCase() + opportunity.status.slice(1)}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
              {isEditing && !stageReadOnlyReason ? (
                <select
                  value={formData.stageId || ''}
                  onChange={(e) => setFormData({ ...formData, stageId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {sortedStages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
              ) : (
                <>
                  <p className="text-gray-900">{opportunity.stage?.name || 'Unknown stage'}</p>
                  {isEditing && stageReadOnlyReason && (
                    <p className="mt-1 text-xs text-gray-500" data-testid="stage-read-only-reason">{stageReadOnlyReason}</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Expected Close Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Close Date</label>
            {isEditing ? (
              <input
                type="date"
                value={formatDateForInput(formData.expectedCloseDate)}
                onChange={(e) => setFormData({ ...formData, expectedCloseDate: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <p className="text-gray-900">
                {opportunity.expectedCloseDate
                  ? new Date(opportunity.expectedCloseDate).toLocaleDateString()
                  : 'No date set'}
              </p>
            )}
          </div>

          {/* Contact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
            {isEditing ? (
              <select
                value={formData.contactId || ''}
                onChange={(e) => setFormData({ ...formData, contactId: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">None</option>
                {contacts?.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.fullName}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-gray-900">
                {opportunity.contact?.fullName || 'No contact'}
              </p>
            )}
          </div>

          {/* PitchLess materials.
              Read mode only: the edit form is a single save, and a panel with
              its own asynchronous actions inside it would leave the user
              unsure what "Save" applies to. */}
          {!isEditing && (
            <div className="pt-4 border-t border-gray-200">
              <MaterialsPanel
                entityType="opportunity"
                entityId={opportunity.id}
                entityLabel={opportunity.title || 'this deal'}
              />
            </div>
          )}

          {/* Metadata */}
          {!isEditing && (
            <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p>Created: {new Date(opportunity.createdAt).toLocaleDateString()}</p>
              <p>Updated: {new Date(opportunity.updatedAt).toLocaleDateString()}</p>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
